/**
 * ReservationBooking Component
 * Modal for booking restaurant reservations
 */

import React, { useState, useEffect } from 'react';
import { X, AlertCircle, CheckCircle } from 'lucide-react';
import { checkAvailability } from '../services/opentableService';
import { isFeatureEnabled } from '../config/features';
import { API_BASE_URL } from '../config/environment';
import { OPENTABLE_CONFIG } from '../config/opentable';
import { learnSignal } from '../services/tasteProfileService';
import { trackReservationClick, calculateCommission } from '../services/affiliateService';
import { authFetch } from '../utils/authFetch';

// ── Date helpers ──────────────────────────────────────────────────────────────

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
};

const getMinDate = () => new Date().toISOString().split('T')[0];

const getMaxDate = () => {
  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() + 30);
  return maxDate.toISOString().split('T')[0];
};

// ── Fire-and-forget side effects after successful booking ─────────────────────

function firePostBookingSideEffects(restaurant, trip, userProfile, createdReservation) {
  learnSignal('dining_booking', {
    cuisine: restaurant.cuisine,
    priceRange: restaurant.priceRange,
    location: trip?.destination || ''
  });

  const estimatedCommission = calculateCommission(
    restaurant.priceRange,
    OPENTABLE_CONFIG.affiliate.commissionRate
  );
  authFetch(`${API_BASE_URL}/opentable-booking`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      eventType: 'reservation_confirmed',
      reservationId: createdReservation.reservationId,
      confirmationNumber: createdReservation.confirmationNumber,
      restaurantId: restaurant.id || restaurant.openTableId,
      restaurantName: restaurant.name,
      userId: userProfile?.sub,
      tripId: trip?.tripId,
      estimatedCommission,
      timestamp: new Date().toISOString()
    })
  }).catch(err => console.warn('[ReservationBooking] Failed to record reservation_confirmed event:', err.message));

  trackReservationClick({
    userId: userProfile?.sub,
    tripId: trip?.tripId,
    restaurantId: restaurant.id || restaurant.openTableId,
    restaurantName: restaurant.name,
    destinationCity: trip?.destination || '',
    linkUrl: ''
  });
}

// ── Step sub-components ───────────────────────────────────────────────────────

const DateTimeStep = ({ restaurant, selectedDate, selectedTime, partySize, loading, availableSlots, onDateChange, onTimeSelect, onPartySizeChange }) => (
  <>
    <div style={{ marginBottom: '20px' }}>
      <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '8px', color: '#374151' }}>
        Restaurant
      </label>
      <div style={{ padding: '12px', backgroundColor: '#f9fafb', borderRadius: '6px', fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
        {restaurant.name}
      </div>
    </div>

    <div style={{ marginBottom: '20px' }}>
      <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '8px', color: '#374151' }}>
        Date <span style={{ color: '#dc2626' }}>*</span>
      </label>
      <input
        type="date"
        value={selectedDate}
        onChange={onDateChange}
        min={getMinDate()}
        max={getMaxDate()}
        style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
      />
    </div>

    <div style={{ marginBottom: '20px' }}>
      <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '8px', color: '#374151' }}>
        Party Size <span style={{ color: '#dc2626' }}>*</span>
      </label>
      <select
        value={partySize}
        onChange={(e) => onPartySizeChange(parseInt(e.target.value))}
        style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
      >
        {[1, 2, 3, 4, 5, 6, 7, 8].map(size => (
          <option key={size} value={size}>
            {size} {size === 1 ? 'Guest' : 'Guests'}
          </option>
        ))}
      </select>
    </div>

    {selectedDate && (
      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '8px', color: '#374151' }}>
          Available Times <span style={{ color: '#dc2626' }}>*</span>
        </label>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
            Loading availability...
          </div>
        ) : availableSlots.length > 0 ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '8px' }}>
            {availableSlots.map((slot, index) => (
              <button
                key={index}
                onClick={() => onTimeSelect(slot.time)}
                style={{
                  padding: '10px',
                  border: selectedTime === slot.time ? '2px solid #d32f2f' : '1px solid #d1d5db',
                  borderRadius: '6px',
                  backgroundColor: selectedTime === slot.time ? '#fef2f2' : '#ffffff',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: selectedTime === slot.time ? '600' : '400',
                  color: selectedTime === slot.time ? '#d32f2f' : '#374151'
                }}
              >
                {slot.time}
              </button>
            ))}
          </div>
        ) : (
          <div style={{ padding: '12px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', color: '#991b1b', fontSize: '14px' }}>
            No availability for this date. Please select another date.
          </div>
        )}
      </div>
    )}
  </>
);

const DetailsStep = ({ selectedDate, selectedTime, partySize, customerInfo, specialRequests, onCustomerChange, onSpecialRequestsChange }) => (
  <>
    <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f9fafb', borderRadius: '6px', fontSize: '14px', color: '#6b7280' }}>
      <div><strong>Date:</strong> {formatDate(selectedDate)}</div>
      <div><strong>Time:</strong> {selectedTime}</div>
      <div><strong>Party Size:</strong> {partySize} {partySize === 1 ? 'Guest' : 'Guests'}</div>
    </div>

    {[
      { field: 'firstName', label: 'First Name', type: 'text', required: true },
      { field: 'lastName', label: 'Last Name', type: 'text', required: true },
      { field: 'email', label: 'Email', type: 'email', required: true },
      { field: 'phone', label: 'Phone Number', type: 'tel', required: false, placeholder: '(555) 123-4567' },
    ].map(({ field, label, type, required, placeholder }) => (
      <div key={field} style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px', color: '#374151' }}>
          {label} {required && <span style={{ color: '#dc2626' }}>*</span>}
        </label>
        <input
          type={type}
          value={customerInfo[field]}
          onChange={(e) => onCustomerChange({ ...customerInfo, [field]: e.target.value })}
          placeholder={placeholder}
          style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
        />
      </div>
    ))}

    <div style={{ marginBottom: '20px' }}>
      <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px', color: '#374151' }}>
        Special Requests
      </label>
      <textarea
        value={specialRequests}
        onChange={(e) => onSpecialRequestsChange(e.target.value)}
        placeholder="Dietary restrictions, seating preferences, special occasions, etc."
        rows={4}
        style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', fontFamily: 'inherit', resize: 'vertical' }}
      />
    </div>
  </>
);

const ConfirmationStep = ({ reservation, restaurant }) => {
  const isOpenTableSourced = restaurant.dataSource === 'opentable-live' || restaurant.dataSource === 'opentable-cache';

  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{
        width: '64px', height: '64px', borderRadius: '50%', backgroundColor: '#dcfce7',
        display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px'
      }}>
        <CheckCircle size={32} color="#16a34a" />
      </div>
      <h3 style={{ margin: '0 0 12px', fontSize: '20px', fontWeight: '600', color: '#1f2937' }}>
        Reservation Confirmed!
      </h3>
      <div style={{ marginBottom: '20px', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '6px', fontSize: '14px', color: '#374151' }}>
        <div style={{ marginBottom: '8px' }}><strong>Confirmation Number:</strong> {reservation.confirmationNumber}</div>
        <div style={{ marginBottom: '8px' }}><strong>Restaurant:</strong> {restaurant.name}</div>
        <div style={{ marginBottom: '8px' }}><strong>Date:</strong> {formatDate(reservation.date)}</div>
        <div style={{ marginBottom: '8px' }}><strong>Time:</strong> {reservation.time}</div>
        <div><strong>Party Size:</strong> {reservation.partySize} {reservation.partySize === 1 ? 'Guest' : 'Guests'}</div>
      </div>
      <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 20px' }}>
        A confirmation email has been sent to {reservation.customer.email}
      </p>
      {isOpenTableSourced && (
        <p style={{
          fontSize: `${Math.max(OPENTABLE_CONFIG.branding.minTaglineFontSize, 11)}px`,
          color: '#9ca3af', margin: '0 0 10px', textAlign: 'center'
        }}>
          {OPENTABLE_CONFIG.branding.tagline}
        </p>
      )}
    </div>
  );
};

// ── Step header titles ────────────────────────────────────────────────────────

const STEP_TITLES = {
  1: 'Select Date & Time',
  2: 'Reservation Details',
  3: 'Reservation Confirmed',
};

// ── Step content renderer ──────────────────────────────────────────────────────

const StepContent = ({ step, reservation, restaurant, selectedDate, selectedTime, partySize, loading, availableSlots, customerInfo, specialRequests, handleDateChange, setSelectedTime, setPartySize, setCustomerInfo, setSpecialRequests }) => {
  if (step === 1) {
    return (
      <DateTimeStep
        restaurant={restaurant}
        selectedDate={selectedDate}
        selectedTime={selectedTime}
        partySize={partySize}
        loading={loading}
        availableSlots={availableSlots}
        onDateChange={handleDateChange}
        onTimeSelect={setSelectedTime}
        onPartySizeChange={setPartySize}
      />
    );
  }
  if (step === 2) {
    return (
      <DetailsStep
        selectedDate={selectedDate}
        selectedTime={selectedTime}
        partySize={partySize}
        customerInfo={customerInfo}
        specialRequests={specialRequests}
        onCustomerChange={setCustomerInfo}
        onSpecialRequestsChange={setSpecialRequests}
      />
    );
  }
  if (step === 3 && reservation) {
    return <ConfirmationStep reservation={reservation} restaurant={restaurant} />;
  }
  return null;
};

// ── Reservation data builder ──────────────────────────────────────────────────

function buildReservationData(restaurant, selectedDate, selectedTime, partySize, customerInfo, specialRequests, userProfile, trip) {
  return {
    restaurantId: restaurant.id || restaurant.openTableId,
    date: selectedDate,
    time: selectedTime,
    partySize,
    customer: {
      firstName: customerInfo.firstName,
      lastName: customerInfo.lastName,
      email: customerInfo.email,
      phone: customerInfo.phone || undefined
    },
    specialRequests: specialRequests || undefined,
    userId: userProfile?.sub,
    tripId: trip?.tripId
  };
}

// ── Main component ────────────────────────────────────────────────────────────

const ReservationBooking = ({ restaurant, trip, userProfile, onClose, onReservationCreated }) => {
  const [step, setStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [partySize, setPartySize] = useState(2);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [customerInfo, setCustomerInfo] = useState({
    firstName: userProfile?.given_name || '',
    lastName: userProfile?.family_name || '',
    email: userProfile?.email || '',
    phone: ''
  });
  const [specialRequests, setSpecialRequests] = useState('');
  const [reservation, setReservation] = useState(null);

  const loadAvailability = React.useCallback(async () => {
    if (!selectedDate || !restaurant) return;
    setLoading(true);
    setError(null);
    try {
      const slots = await checkAvailability(restaurant.id || restaurant.openTableId, selectedDate, partySize);
      setAvailableSlots(slots);
    } catch (err) {
      console.error('Error loading availability:', err);
      setError('Failed to load availability. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [selectedDate, partySize, restaurant]);

  useEffect(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setSelectedDate(tomorrow.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    if (selectedDate && restaurant) {
      loadAvailability();
    }
  }, [selectedDate, partySize, restaurant, loadAvailability]);

  if (!isFeatureEnabled('OPENTABLE_ENABLED')) {
    return null;
  }

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
    setSelectedTime('');
  };

  const handleSubmit = async () => {
    if (!customerInfo.firstName || !customerInfo.lastName || !customerInfo.email) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const reservationData = buildReservationData(
        restaurant, selectedDate, selectedTime, partySize,
        customerInfo, specialRequests, userProfile, trip
      );

      const response = await fetch(`${API_BASE_URL}/opentable/reservations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reservationData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create reservation');
      }

      const createdReservation = await response.json();
      setReservation(createdReservation);
      setStep(3);

      firePostBookingSideEffects(restaurant, trip, userProfile, createdReservation);

      if (onReservationCreated) {
        onReservationCreated(createdReservation);
      }
    } catch (err) {
      console.error('Error creating reservation:', err);
      setError(err.message || 'Failed to create reservation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (step === 1) {
      if (!selectedDate || !selectedTime) {
        setError('Please select a date and time');
        return;
      }
      setStep(2);
      setError(null);
    } else if (step === 2) {
      handleSubmit();
    }
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: '20px'
    }}
    onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        backgroundColor: '#ffffff', borderRadius: '12px', width: '100%', maxWidth: '500px',
        maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px', borderBottom: '1px solid #e5e7eb',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#1f2937' }}>
            {STEP_TITLES[step]}
          </h2>
          <button
            onClick={onClose}
            style={{ border: 'none', backgroundColor: 'transparent', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <X size={20} color="#6b7280" />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '20px' }}>
          <StepContent
            step={step}
            reservation={reservation}
            restaurant={restaurant}
            selectedDate={selectedDate}
            selectedTime={selectedTime}
            partySize={partySize}
            loading={loading}
            availableSlots={availableSlots}
            customerInfo={customerInfo}
            specialRequests={specialRequests}
            handleDateChange={handleDateChange}
            setSelectedTime={setSelectedTime}
            setPartySize={setPartySize}
            setCustomerInfo={setCustomerInfo}
            setSpecialRequests={setSpecialRequests}
          />

          {/* Error Message */}
          {error && (
            <div style={{
              marginBottom: '16px', padding: '12px', backgroundColor: '#fef2f2',
              border: '1px solid #fecaca', borderRadius: '6px', color: '#991b1b',
              fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px'
            }}>
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          {/* Actions */}
          {step < 3 && (
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button
                onClick={onClose}
                style={{
                  flex: 1, padding: '12px', border: '1px solid #d1d5db', borderRadius: '6px',
                  backgroundColor: '#ffffff', color: '#374151', fontSize: '15px', fontWeight: '500', cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleNext}
                disabled={loading}
                style={{
                  flex: 1, padding: '12px', border: 'none', borderRadius: '6px',
                  backgroundColor: loading ? '#9ca3af' : '#d32f2f', color: '#ffffff',
                  fontSize: '15px', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? 'Processing...' : step === 1 ? 'Continue' : 'Confirm Reservation'}
              </button>
            </div>
          )}

          {step === 3 && (
            <button
              onClick={onClose}
              style={{
                width: '100%', padding: '12px', border: 'none', borderRadius: '6px',
                backgroundColor: '#d32f2f', color: '#ffffff', fontSize: '15px',
                fontWeight: '600', cursor: 'pointer', marginTop: '20px'
              }}
            >
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReservationBooking;
